import React, {ReactNode, useEffect, useMemo, useState} from "react";
import {
	Autocomplete,
	Avatar,
	Box,
	Button,
	ButtonBase,
	Card,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Link,
	Skeleton,
	Stack,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import {alpha, useTheme} from "@mui/material/styles";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import {
	AdminSharedRestControllerApiFactory,
	Interview,
	InterviewRestControllerApiFactory,
	Person,
	PersonRestControllerApiFactory,
	PersonSummary,
} from "arteco-api-client-ts";
import {Link as RouterLink, useNavigate, useParams} from "react-router-dom";
import dayjs from "dayjs";
import {useApiManager} from "../../../../components/utils/api";
import {parsePathId} from "../../../../components/form/form_utils";
import {dateToString} from "../../../../components/utils/dates";
import {CustomNumberFormat} from "../../../../components/utils/custom_number_format";
import {INTERVIEW_EXIT_RISK_LABELS, INTERVIEW_HEALTH_LABELS} from "../../../../components/form/types";
import AdminInner from "../../../../components/layout/admin_inner";
import {btnSx, fullName, initials, panelSx} from "./ui";

type Tone = "success" | "warning" | "error";

const HEALTH_OPTIONS: { value: string, tone: Tone, hint: string }[] = [
	{value: "GREEN", tone: "success", hint: "Está bien"},
	{value: "AMBER", tone: "warning", hint: "Algo le preocupa"},
	{value: "RED", tone: "error", hint: "Necesita atención"},
];

const RISK_OPTIONS: { value: string, tone: Tone, hint: string }[] = [
	{value: "LOW", tone: "success", hint: "No se plantea irse"},
	{value: "MEDIUM", tone: "warning", hint: "Lo ha pensado"},
	{value: "HIGH", tone: "error", hint: "Puede irse pronto"},
];

const NEXT_SHORTCUTS = [
	{label: "En 2 semanas", days: 14},
	{label: "En 6 semanas", days: 42},
	{label: "En 3 meses", days: 91},
];

const today = () => dayjs().format("YYYY-MM-DD");

const Section = ({title, subtitle, icon, children}: { title: string, subtitle?: string, icon?: ReactNode, children: ReactNode }) => (
	<Card elevation={0} sx={panelSx}>
		<Stack direction="row" spacing={1} alignItems="center">
			{icon}
			<Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
		</Stack>
		{subtitle && <Typography variant="body2" color="text.secondary" sx={{mt: 0.25}}>{subtitle}</Typography>}
		<Box sx={{mt: 2}}>{children}</Box>
	</Card>
);

/** Tres tarjetas seleccionables con su color: más rápido y más claro que un desplegable. */
const Semaphore = ({label, options, labels, value, onChange}: {
	label: string,
	options: { value: string, tone: Tone, hint: string }[],
	labels: Record<string, string>,
	value?: string,
	onChange: (v?: string) => void,
}) => {
	const theme = useTheme();
	return (
		<Box>
			<Typography variant="body2" fontWeight={600} sx={{mb: 1}}>{label}</Typography>
			<Box role="radiogroup" aria-label={label} sx={{display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1}}>
				{options.map(o => {
					const c = theme.palette[o.tone].main;
					const selected = value === o.value;
					return (
						<ButtonBase key={o.value} role="radio" aria-checked={selected}
									onClick={() => onChange(selected ? undefined : o.value)}
									sx={{
										p: 1.5, borderRadius: 2, textAlign: "left", justifyContent: "flex-start",
										border: "1.5px solid", borderColor: selected ? c : "divider",
										bgcolor: selected ? alpha(c, 0.08) : "background.paper",
										transition: "all .15s", "&:hover": {borderColor: c},
									}}>
							<Box sx={{minWidth: 0}}>
								<Stack direction="row" spacing={1} alignItems="center">
									<Box sx={{width: 10, height: 10, borderRadius: "50%", bgcolor: c, flexShrink: 0}}/>
									<Typography variant="body2" fontWeight={700}>{labels[o.value]}</Typography>
								</Stack>
								<Typography variant="caption" color="text.secondary" sx={{display: {xs: "none", sm: "block"}, mt: 0.5}}>
									{o.hint}
								</Typography>
							</Box>
						</ButtonBase>
					);
				})}
			</Box>
		</Box>
	);
};

/** Buscador de personas de la plantilla, con el mismo servicio que el resto de formularios. */
export const PersonPicker = ({label, value, onChange, placeholder, helperText}: {
	label: string,
	value?: { id?: number, label: string },
	onChange: (v?: { id?: number, label: string }) => void,
	placeholder?: string,
	helperText?: string,
}) => {
	const apiManager = useApiManager();
	const ctrl = apiManager.factory(AdminSharedRestControllerApiFactory);
	const [input, setInput] = useState("");
	const [options, setOptions] = useState<{ id?: number, label: string }[]>([]);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		let active = true;
		setLoading(true);
		const t = setTimeout(() => {
			ctrl.retrievePeople(input || undefined).then(resp => {
				if (active) {
					setOptions(resp.data.map(p => ({id: p.id, label: fullName(p)})));
				}
			}).catch(() => undefined).finally(() => active && setLoading(false));
		}, 250);
		return () => {
			active = false;
			clearTimeout(t);
		};
	}, [input]);

	return (
		<Autocomplete
			value={value || null}
			options={options}
			loading={loading}
			filterOptions={x => x}
			isOptionEqualToValue={(a, b) => a.id === b.id}
			onChange={(_, v) => onChange(v || undefined)}
			onInputChange={(_, v) => setInput(v)}
			renderInput={params => <TextField {...params} label={label} placeholder={placeholder} helperText={helperText}
											  InputLabelProps={{...params.InputLabelProps, shrink: true}}/>}
		/>
	);
};

/** La persona entrevistada: lo que conviene tener delante (alta, salario, vacaciones) y sus entrevistas anteriores. */
const PersonPanel = ({personId, currentId}: { personId?: number, currentId?: number }) => {
	const apiManager = useApiManager();
	const theme = useTheme();
	const [summary, setSummary] = useState<PersonSummary>();
	const [history, setHistory] = useState<Interview[]>();

	useEffect(() => {
		setSummary(undefined);
		setHistory(undefined);
		if (!personId) {
			return;
		}
		apiManager.execute(apiManager.factory(PersonRestControllerApiFactory).summaryPerson(personId)).then(setSummary).catch(() => undefined);
		apiManager.execute(apiManager.factory(InterviewRestControllerApiFactory).listInterview(personId))
			.then(list => setHistory(list
				.filter(i => i.id !== currentId && !i.dismissed)
				.sort((a, b) => (b.interviewDate || "").localeCompare(a.interviewDate || ""))))
			.catch(() => setHistory([]));
	}, [personId, currentId]);

	if (!personId) {
		return <Card elevation={0} sx={panelSx}>
			<Typography variant="body2" color="text.secondary">Elige la persona para ver su ficha y sus entrevistas anteriores.</Typography>
		</Card>;
	}
	const p = summary?.person;
	const Fact = ({label, children}: { label: string, children: ReactNode }) => (
		<Box>
			<Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
			<Typography variant="body2" fontWeight={600} component="div">{children}</Typography>
		</Box>
	);
	const dot = (tone?: string) => tone ? theme.palette[(HEALTH_OPTIONS.concat(RISK_OPTIONS).find(o => o.value === tone)?.tone) || "success"].main : theme.palette.grey[300];

	return (
		<Stack spacing={3}>
			<Card elevation={0} sx={panelSx}>
				{!summary ? <Skeleton variant="rounded" height={120}/> : (
					<>
						<Stack direction="row" spacing={1.5} alignItems="center">
							<Avatar sx={{width: 48, height: 48, fontWeight: 700, bgcolor: "primary.main"}}>{initials(fullName(p))}</Avatar>
							<Box sx={{minWidth: 0}}>
								<Link component={RouterLink} to={"/app/admin/employee/person/" + personId} underline="hover"
									  color="text.primary" fontWeight={700} noWrap sx={{display: "block"}}>
									{fullName(p)}
								</Link>
								<Typography variant="caption" color="text.secondary" noWrap display="block">{p?.email}</Typography>
							</Box>
						</Stack>
						<Box sx={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mt: 2.5}}>
							<Fact label="Alta">{dateToString(p?.hireDate)}</Fact>
							<Fact label="Cumpleaños">{p?.birth ? dayjs(p.birth).format("D [de] MMMM") : "—"}</Fact>
							<Fact label="Salario anual"><CustomNumberFormat amount={summary.salary?.annualSalary}/></Fact>
							<Fact label="Revisado">{dateToString(summary.lastSalaryUpdate) || "—"}</Fact>
							<Fact label="Vacaciones">{summary.takenVacations} de {summary.allowedVacations} días</Fact>
						</Box>
					</>
				)}
			</Card>

			<Card elevation={0} sx={panelSx}>
				<Typography variant="subtitle1" fontWeight={700}>Entrevistas anteriores</Typography>
				{!history ? <Skeleton variant="rounded" height={80} sx={{mt: 2}}/> : history.length === 0 ? (
					<Typography variant="body2" color="text.secondary" sx={{mt: 1}}>Es la primera.</Typography>
				) : (
					<Stack spacing={0} sx={{mt: 2}}>
						{history.slice(0, 6).map((i, idx) => (
							<Stack key={i.id} direction="row" spacing={1.5} sx={{position: "relative", pb: idx < Math.min(history.length, 6) - 1 ? 2 : 0}}>
								<Box sx={{position: "relative", pt: 0.5}}>
									<Box sx={{width: 10, height: 10, borderRadius: "50%", bgcolor: dot(i.health), position: "relative", zIndex: 1}}/>
									{idx < Math.min(history.length, 6) - 1 &&
										<Box sx={{position: "absolute", left: 4, top: 14, bottom: -16, width: 2, bgcolor: "divider"}}/>}
								</Box>
								<Box sx={{minWidth: 0, flexGrow: 1}}>
									<Link component={RouterLink} underline="hover" variant="body2" fontWeight={600}
										  to={"/app/admin/employee/person/" + personId + "/interview/" + i.id}>
										{dateToString(i.interviewDate)}
									</Link>
									<Typography variant="caption" color="text.secondary" display="block">
										{[i.health && "Salud " + INTERVIEW_HEALTH_LABELS[i.health]?.toLowerCase(),
											i.exitRisk && "riesgo " + INTERVIEW_EXIT_RISK_LABELS[i.exitRisk]?.toLowerCase(),
											i.interviewer && "con " + fullName(i.interviewer)].filter(Boolean).join(" · ") || "Sin valoración"}
									</Typography>
									{(i.opportunities || i.privateComments) && (
										<Typography variant="caption" color="text.secondary" sx={{
											display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", mt: 0.25,
										}}>
											{i.opportunities || i.privateComments}
										</Typography>
									)}
								</Box>
							</Stack>
						))}
						{history.length > 6 && (
							<Link component={RouterLink} to={"/app/admin/employee/person/" + personId + "/interview"} variant="body2" sx={{mt: 2}}>
								Ver las {history.length}
							</Link>
						)}
					</Stack>
				)}
			</Card>
		</Stack>
	);
};

/**
 * Ficha de una entrevista periódica (#13808): fecha y entrevistador, semáforo de salud y riesgo de
 * salida, oportunidades, próxima acordada y comentarios privados; al lado, la persona y su historial.
 */
export const InterviewEditPage = () => {
	const params = useParams();
	const navigate = useNavigate();
	const apiManager = useApiManager();
	const ctrl = apiManager.factory(InterviewRestControllerApiFactory);
	const pathPersonId = parsePathId(params, "personId");
	const id = parsePathId(params);

	const [bean, setBean] = useState<Interview>();
	const [person, setPerson] = useState<{ id?: number, label: string }>();
	const [interviewer, setInterviewer] = useState<{ id?: number, label: string }>();
	const [saving, setSaving] = useState(false);
	const [confirmDelete, setConfirmDelete] = useState(false);
	const [dirty, setDirty] = useState(false);

	useEffect(() => {
		if (id) {
			apiManager.execute(ctrl.getInterview(id)).then(i => {
				setBean(i);
				setPerson(i.person ? {id: i.person.id, label: fullName(i.person)} : undefined);
				setInterviewer(i.interviewer ? {id: i.interviewer.id, label: fullName(i.interviewer)} : undefined);
				setDirty(false);
			}).catch(() => undefined);
		} else {
			setBean({person: {id: pathPersonId} as Person, interviewDate: today(), privateComments: "", healthReason: "", opportunities: ""});
			setInterviewer(undefined);
			setDirty(false);
		}
	}, [id]);

	// Con la persona en la ruta, su nombre sale del resumen (lo pide el panel lateral de todos modos).
	useEffect(() => {
		if (!id && pathPersonId) {
			apiManager.execute(apiManager.factory(PersonRestControllerApiFactory).summaryPerson(pathPersonId))
				.then(s => setPerson({id: pathPersonId, label: fullName(s.person)})).catch(() => undefined);
		}
	}, [id, pathPersonId]);

	const set = (patch: Partial<Interview>) => {
		setBean(b => ({...(b as Interview), ...patch}));
		setDirty(true);
	};

	const personId = person?.id;
	const valid = !!personId && !!bean?.interviewDate;

	const save = () => {
		if (!bean || !valid) {
			return;
		}
		setSaving(true);
		const body: Interview = {
			...bean,
			person: {id: personId} as Person,
			interviewer: interviewer?.id ? {id: interviewer.id} as Person : undefined,
		};
		apiManager.execute(ctrl.saveInterview(body), "Entrevista guardada")
			.then(saved => {
				setBean(saved);
				setDirty(false);
				if (!id && saved.id) {
					navigate("/app/admin/employee/person/" + personId + "/interview/" + saved.id, {replace: true});
				}
			})
			.catch(() => undefined)
			.finally(() => setSaving(false));
	};

	const remove = () => {
		if (!id) {
			return;
		}
		apiManager.execute(ctrl.deleteOrDismissInterview(id), "Entrevista eliminada")
			.then(() => navigate("/app/admin/employee"))
			.catch(() => undefined);
	};

	const title = useMemo(() => person?.label ? `Entrevista con ${person.label}` : "Nueva entrevista", [person]);

	if (!bean) {
		return <AdminInner withoutPaper><Box display="flex" justifyContent="center" p={6}><CircularProgress/></Box></AdminInner>;
	}

	const nextDays = bean.nextPlannedDate ? dayjs(bean.nextPlannedDate).diff(dayjs(bean.interviewDate), "day") : undefined;

	return (
		<AdminInner withoutPaper>
		<Box sx={{maxWidth: 1180}}>
			<Stack direction={{xs: "column", sm: "row"}} spacing={2} justifyContent="space-between" alignItems={{sm: "flex-end"}} sx={{mb: 3}}>
				<Box sx={{minWidth: 0}}>
					<Button size="small" startIcon={<ArrowBackRoundedIcon/>} onClick={() => navigate(-1)}
							sx={{...btnSx, ml: -1, mb: 0.5, color: "text.secondary"}}>
						Volver
					</Button>
					<Typography variant="h5" component="h1" fontWeight={700}>{title}</Typography>
					<Typography variant="body2" color="text.secondary">
						{id ? (dirty ? "Cambios sin guardar" : "Guardada") : "Solo la ve RR. HH.; a la persona no le llega nada de lo que se anota aquí."}
					</Typography>
				</Box>
				<Stack direction="row" spacing={1} sx={{flexShrink: 0}}>
					{id && (
						<Button color="error" startIcon={<DeleteOutlineRoundedIcon/>} onClick={() => setConfirmDelete(true)} sx={btnSx}>
							Eliminar
						</Button>
					)}
					<Button variant="contained" disableElevation disabled={!valid || saving || (!!id && !dirty)} onClick={save}
							sx={{...btnSx, px: 3}}>
						{saving ? <CircularProgress size={20} color="inherit"/> : id ? "Guardar cambios" : "Guardar entrevista"}
					</Button>
				</Stack>
			</Stack>

			<Grid container spacing={3}>
				<Grid item xs={12} md={8}>
					<Stack spacing={3}>
						<Section title="Datos">
							<Grid container spacing={2}>
								{!pathPersonId && !id && (
									<Grid item xs={12}>
										<PersonPicker label="Persona" value={person}
													  onChange={v => {
														  setPerson(v);
														  setDirty(true);
													  }}/>
									</Grid>
								)}
								<Grid item xs={12} sm={5}>
									<TextField fullWidth type="date" label="Fecha" value={bean.interviewDate?.substring(0, 10) || ""}
											   InputLabelProps={{shrink: true}} inputProps={{max: today()}}
											   onChange={e => set({interviewDate: e.target.value})}/>
								</Grid>
								<Grid item xs={12} sm={7}>
									<PersonPicker label="Entrevistador" value={interviewer}
												  placeholder="Quien entrevista habitualmente"
												  helperText="Vacío: el entrevistador por defecto (interviews.default-interviewer)"
												  onChange={v => {
													  setInterviewer(v);
													  setDirty(true);
												  }}/>
								</Grid>
							</Grid>
						</Section>

						<Section title="Cómo está" subtitle="La salud y el riesgo de salida alimentan el panel y el resumen de los lunes.">
							<Stack spacing={2.5}>
								<Semaphore label="Salud" options={HEALTH_OPTIONS} labels={INTERVIEW_HEALTH_LABELS}
										   value={bean.health} onChange={v => set({health: v as Interview["health"]})}/>
								<Semaphore label="Riesgo de salida" options={RISK_OPTIONS} labels={INTERVIEW_EXIT_RISK_LABELS}
										   value={bean.exitRisk} onChange={v => set({exitRisk: v as Interview["exitRisk"]})}/>
								<TextField fullWidth multiline minRows={2} label="Motivo" value={bean.healthReason || ""}
										   placeholder="Por qué ese color: carga de trabajo, proyecto, equipo, personal…"
										   InputLabelProps={{shrink: true}}
										   onChange={e => set({healthReason: e.target.value})}/>
							</Stack>
						</Section>

						<Section title="Oportunidades" subtitle="Formación, proyecto, cambio de rol, algo comercial… Salen en el resumen de los lunes.">
							<TextField fullWidth multiline minRows={3} value={bean.opportunities || ""}
									   placeholder="Quiere liderar un equipo; le interesa la certificación de AWS…"
									   onChange={e => set({opportunities: e.target.value})}/>
						</Section>

						<Section title="Próxima entrevista" subtitle="Si se acuerda una fecha, manda sobre la cadencia; si no, se calcula sola.">
							<Stack direction={{xs: "column", sm: "row"}} spacing={1.5} alignItems={{sm: "center"}}>
								<TextField type="date" label="Acordada para" value={bean.nextPlannedDate?.substring(0, 10) || ""}
										   InputLabelProps={{shrink: true}} sx={{minWidth: 200}}
										   InputProps={{
											   endAdornment: bean.nextPlannedDate ? (
												   <Tooltip title="Quitar: que la calcule la cadencia">
													   <IconButton size="small" onClick={() => set({nextPlannedDate: undefined})}>
														   <CloseRoundedIcon fontSize="small"/>
													   </IconButton>
												   </Tooltip>
											   ) : undefined,
										   }}
										   onChange={e => set({nextPlannedDate: e.target.value || undefined})}/>
								<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
									{NEXT_SHORTCUTS.map(s => (
										<Button key={s.days} size="small" variant={nextDays === s.days ? "contained" : "outlined"} disableElevation
												onClick={() => set({nextPlannedDate: dayjs(bean.interviewDate).add(s.days, "day").format("YYYY-MM-DD")})}
												sx={btnSx}>
											{s.label}
										</Button>
									))}
								</Stack>
							</Stack>
						</Section>

						<Section title="Comentarios privados" icon={<LockOutlinedIcon fontSize="small" color="action"/>}
								 subtitle="Solo RR. HH. Lo que se habló, en tus palabras.">
							<TextField fullWidth multiline minRows={6} value={bean.privateComments || ""}
									   onChange={e => set({privateComments: e.target.value})}/>
						</Section>

						<Stack direction="row" justifyContent="flex-end" spacing={1} sx={{display: {md: "none"}}}>
							<Button variant="contained" disableElevation fullWidth disabled={!valid || saving || (!!id && !dirty)} onClick={save}
									sx={{...btnSx, py: 1.5}}>
								{id ? "Guardar cambios" : "Guardar entrevista"}
							</Button>
						</Stack>
					</Stack>
				</Grid>

				<Grid item xs={12} md={4}>
					<Box sx={{position: {md: "sticky"}, top: {md: 16}}}>
						<PersonPanel personId={personId} currentId={id}/>
					</Box>
				</Grid>
			</Grid>

			<Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)} PaperProps={{sx: {borderRadius: 3}}}>
				<DialogTitle sx={{fontWeight: 700}}>¿Eliminar la entrevista?</DialogTitle>
				<DialogContent>
					<Typography variant="body2" color="text.secondary">
						Deja de contar para la cadencia y el panel. La de {dateToString(bean.interviewDate)} con {person?.label}.
					</Typography>
				</DialogContent>
				<DialogActions sx={{px: 3, pb: 2.5}}>
					<Button onClick={() => setConfirmDelete(false)} sx={btnSx}>Cancelar</Button>
					<Button color="error" variant="contained" disableElevation onClick={remove} sx={btnSx}>Eliminar</Button>
				</DialogActions>
			</Dialog>
		</Box>
		</AdminInner>
	);
};
